import React, {
  Component,
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import moment from "moment";
import DateGroup from "@components/DateGroup";
import locale from "antd/es/date-picker/locale/zh_CN";
import RadioBtnGroup from "@components/RadioBtnGroup";
import LineDiagram from "@components/Charts/LineDiagram";
import Loading from "@components/BoxLoading";
import "./statistics.css";
import { Card, Select, Button, message } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { getAllGames } from "@api/statistics";
import {
  getStatisticsByChannel,
  getStatisticsByDate,
} from "@api/newStatistics";
import { formatDate, formatDateStr } from "@utils/tool";
import bus from "@utils/bus";
// import constants from "./constants";
import { store } from "../store-manager";
import { getChannelByLvl } from "@api/user";

const Option = Select.Option;
let jsonAllGames = {};

// 虚拟表格行高
const ROW_HEIGHT = 54;
// 表格可视区域高度
const TABLE_HEIGHT = 500;
// 列宽配置
const MIN_COL_WIDTH = 100;
const MAX_COL_WIDTH = 300;
const CHAR_WIDTH = 14; // 每个字符大约宽度
const PADDING_WIDTH = 32; // padding 宽度

// 计算文本宽度（根据字符数估算）
const getTextWidth = (text) => {
  if (text === undefined || text === null) return 0;
  const str = String(text);
  let width = 0;
  for (let i = 0; i < str.length; i++) {
    // 中文字符宽度较大
    width += str.charCodeAt(i) > 127 ? CHAR_WIDTH : CHAR_WIDTH * 0.6;
  }
  return width;
};

// 虚拟表格组件
const VirtualTable = (props) => {
  const {
    columns,
    dataSource,
    rowClassName: getRowClassName,
    expandable,
  } = props;

  const { expandedRowKeys = [], onExpand } = expandable || {};

  // 容器 ref 用于获取实际宽度
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // 监听容器宽度变化
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // 减去滚动条宽度（约17px）和边框
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    // 使用 ResizeObserver 监听容器大小变化
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // 展开图标样式
  const expandIconStyle = {
    cursor: "pointer",
    fontSize: 14,
    fontWeight: "bold",
    color: "#000",
    marginRight: 8,
    transition: "transform 0.2s",
  };
  const expandIconPlaceholderStyle = { display: "inline-block", width: 22 };

  // 展平所有数据用于计算列宽（包含所有子节点）
  const allFlattenedData = useMemo(() => {
    const result = [];
    const flatten = (items) => {
      items.forEach((item) => {
        result.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      });
    };
    flatten(dataSource || []);
    return result;
  }, [dataSource]);

  // 动态计算每列基础宽度
  const baseColumnWidths = useMemo(() => {
    const widths = {};

    columns.forEach((col) => {
      const key = col.dataIndex;
      // 表头宽度
      let maxWidth = getTextWidth(col.title) + PADDING_WIDTH;

      // 遍历数据计算最大宽度
      allFlattenedData.forEach((row) => {
        const cellValue = row[key];
        const cellWidth = getTextWidth(cellValue) + PADDING_WIDTH;
        if (cellWidth > maxWidth) {
          maxWidth = cellWidth;
        }
      });

      // 第一列需要额外空间放展开图标
      if (columns[0] && columns[0].dataIndex === key) {
        maxWidth += 22;
      }

      // 限制在最小和最大宽度之间
      widths[key] = Math.max(
        MIN_COL_WIDTH,
        Math.min(MAX_COL_WIDTH, Math.ceil(maxWidth))
      );
    });

    return widths;
  }, [columns, allFlattenedData]);

  // 计算基础表格总宽度
  const baseTotalWidth = useMemo(() => {
    return columns.reduce(
      (sum, col) => sum + (baseColumnWidths[col.dataIndex] || MIN_COL_WIDTH),
      0
    );
  }, [columns, baseColumnWidths]);

  // 计算实际列宽（如果基础宽度小于容器宽度，则按比例扩展）
  const columnWidths = useMemo(() => {
    // 如果容器宽度为0或基础宽度已经大于等于容器宽度，直接返回基础宽度
    if (containerWidth <= 0 || baseTotalWidth >= containerWidth) {
      return baseColumnWidths;
    }

    // 计算扩展比例
    const scale = containerWidth / baseTotalWidth;
    const expandedWidths = {};

    columns.forEach((col) => {
      const key = col.dataIndex;
      const baseWidth = baseColumnWidths[key] || MIN_COL_WIDTH;
      // 按比例扩展，但不超过最大宽度的限制可以放宽
      expandedWidths[key] = Math.floor(baseWidth * scale);
    });

    return expandedWidths;
  }, [columns, baseColumnWidths, baseTotalWidth, containerWidth]);

  // 展平树形数据，只展示已展开的节点
  const flattenedData = useMemo(() => {
    const result = [];

    const flatten = (items, level = 0) => {
      items.forEach((item) => {
        const newItem = { ...item, _level: level };
        result.push(newItem);

        // 如果有子节点且已展开，递归展平
        if (
          item.children &&
          item.children.length > 0 &&
          expandedRowKeys.includes(item.key)
        ) {
          flatten(item.children, level + 1);
        }
      });
    };

    flatten(dataSource || []);
    return result;
  }, [dataSource, expandedRowKeys]);

  // 处理展开/收起
  const handleExpand = useCallback(
    (record) => {
      if (!record.children || record.children.length === 0) return;

      const isExpanded = expandedRowKeys.includes(record.key);
      if (onExpand) {
        onExpand(!isExpanded, record);
      }
    },
    [expandedRowKeys, onExpand]
  );

  // 计算表格实际总宽度（取容器宽度和基础宽度的较大值）
  const tableWidth = useMemo(() => {
    const calculatedWidth = columns.reduce(
      (sum, col) => sum + (columnWidths[col.dataIndex] || MIN_COL_WIDTH),
      0
    );
    return Math.max(calculatedWidth, containerWidth);
  }, [columns, columnWidths, containerWidth]);

  // 渲染表头
  const renderHeader = () => (
    <div
      className="virtual-table-header"
      style={{
        display: "flex",
        background: "#fafafa",
        borderBottom: "1px solid #f0f0f0",
        fontWeight: 500,
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {columns.map((col) => {
        const colWidth = columnWidths[col.dataIndex] || MIN_COL_WIDTH;
        return (
          <div
            key={col.key || col.dataIndex}
            style={{
              width: colWidth,
              minWidth: colWidth,
              padding: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {col.title}
          </div>
        );
      })}
    </div>
  );

  // 无数据时显示空状态
  if (!flattenedData || flattenedData.length === 0) {
    return (
      <div
        ref={containerRef}
        className="virtual-table-wrapper"
        style={{
          marginTop: 18,
          borderRadius: 8,
          overflow: "auto",
          boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.1)",
          maxHeight: TABLE_HEIGHT + 54,
          minHeight: TABLE_HEIGHT + 54,
        }}
      >
        <div style={{ minWidth: tableWidth, width: "100%" }}>
          {renderHeader()}
          <div
            style={{
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            暂无数据
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="virtual-table-wrapper"
      style={{
        marginTop: 18,
        borderRadius: 8,
        overflow: "auto", // 统一滚动容器
        boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.1)",
        maxHeight: TABLE_HEIGHT + 54, // 表头高度约52px
        minHeight: TABLE_HEIGHT + 54,
      }}
    >
      <div
        style={{
          minWidth: tableWidth,
          width: containerWidth > baseTotalWidth ? "100%" : "auto",
        }}
      >
        {/* 表头 - 使用 sticky 固定 */}
        <div
          className="virtual-table-header"
          style={{
            display: "flex",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            fontWeight: 500,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          {columns.map((col) => {
            const colWidth = columnWidths[col.dataIndex] || MIN_COL_WIDTH;
            return (
              <div
                key={col.key || col.dataIndex}
                style={{
                  width: colWidth,
                  minWidth: colWidth,
                  padding: "16px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {col.title}
              </div>
            );
          })}
        </div>
        {/* 表格内容 - 直接渲染，不使用虚拟列表的滚动 */}
        {flattenedData.map((record, index) => {
          const rowClassName = getRowClassName(record);
          const hasChildren = record.children && record.children.length > 0;
          const isExpanded = expandedRowKeys.includes(record.key);
          const level = record._level || 0;

          return (
            <div
              key={record.key || index}
              className={`virtual-table-row ${rowClassName}`}
              style={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #f0f0f0",
                cursor: hasChildren ? "pointer" : "default",
                height: ROW_HEIGHT,
              }}
              onClick={() => hasChildren && handleExpand(record)}
            >
              {columns.map((col, colIndex) => {
                const cellValue = record[col.dataIndex];
                const isFirstCol = colIndex === 0;
                const colWidth = columnWidths[col.dataIndex] || MIN_COL_WIDTH;

                return (
                  <div
                    key={col.key || col.dataIndex}
                    style={{
                      width: colWidth,
                      minWidth: colWidth,
                      padding: "8px 16px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isFirstCol &&
                      (hasChildren ? (
                        isExpanded ? (
                          <DownOutlined style={expandIconStyle} />
                        ) : (
                          <RightOutlined style={expandIconStyle} />
                        )
                      ) : level > 0 ? (
                        <span style={expandIconPlaceholderStyle} />
                      ) : null)}
                    <span>
                      {cellValue !== undefined && cellValue !== null
                        ? cellValue
                        : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default class NewStatisticsSummary extends Component {
  // 金额转换函数：从分转换为元，避免浮点精度问题
  convertCentsToYuan = (cents) => {
    if (cents === undefined || cents === null || cents === "") return 0;
    const centsNum = Number(cents);
    if (isNaN(centsNum)) return 0;
    const centsInt = Math.round(centsNum);
    return (centsInt / 100).toFixed(2);
  };

  // 统一字段格式化方法
  formatFieldValue = (key, value) => {
    if (value === undefined || value === null) return 0;
    const { moneyKeys, integerKeys } = this.state;

    // 金额字段：分转元
    if (moneyKeys.includes(key)) {
      return this.convertCentsToYuan(value);
    }
    // 整数字段
    if (integerKeys.includes(key)) {
      return Math.round(Number(value) || 0);
    }
    // 广告收入相关字段（已经是元，保留2位小数）
    if (key === "adIncome" || key === "newUserAdIncome") {
      return Number(value || 0).toFixed(2);
    }
    // 其他数值字段
    const num = Number(value);
    if (!isNaN(num)) {
      return Number.isInteger(num) ? num : num.toFixed(2);
    }
    return value;
  };

  // 格式化数据行中的所有指标字段
  formatRowFields = (row) => {
    const item = { ...row };
    const fieldKeys = this.state.targetBtns.map((btn) => btn.key);

    fieldKeys.forEach((key) => {
      if (item[key] !== undefined) {
        item[key] = this.formatFieldValue(key, item[key]);
      }
    });

    return item;
  };

  // 自定义维度配置
  customDimensionConfig = {
    // fejsfmerge 游戏 + 武汉微派网络科技有限公司
    fejsfmerge: {
      channelName: "武汉微派网络科技有限公司",
      options: [
        { value: "", label: "全部入口" },
        { value: "CCBgAAoXkpQY8uTnpRoiOR", label: "入口1" },
        { value: "CCBgAAoXkpQY8uTnpRoiOS", label: "入口2" },
        { value: "CCBgAAoXkpQY8uTnpRoiOT", label: "入口3" },
      ],
    },
    // pirate-minigame 游戏 + 武汉微派网络科技有限公司
    "pirate-minigame": {
      channelName: "武汉微派网络科技有限公司",
      options: [
        { value: "", label: "全部入口" },
        { value: "CCBgAAoXkpQY93Eg6_iA9l", label: "入口1" },
        { value: "CCBgAAoXkpQY93Eg6_iA9k", label: "入口2" },
      ],
    },
  };

  state = {
    isChartLoading: false,
    tagType: true,
    isTableLoading: false,
    selectedVal: {
      time: { start: 0, end: 0 },
      game: [], // 改为数组，支持多选
      firstLevel: "allFirstLevel",
      secondLevel: "",
      thirdLevel: ["allThirdLevel"],
    },
    system: "", // 平台筛选：空字符串表示全部，android/ios 表示具体平台
    customDimension: "", // 自定义维度筛选
    allGames: [],
    allSelect: [
      {
        mode: "multiple", // 多选模式
        key: "game",
        placeholder: "请选游戏",
        options: [],
        defaultValue: [],
      },
      {
        key: "firstLevel",
        placeholder: "请选一级渠道",
        defaultValue: "allFirstLevel",
        options: [{ value: "allFirstLevel", label: "全部一级渠道" }],
      },
      {
        mode: "",
        key: "secondLevel",
        placeholder: "请选二级渠道",
        defaultValue: "", //"allSecondLevel",
        options: [], //[{ value: "allSecondLevel", label: "全部二级渠道" }],
      },
      {
        mode: "multiple",
        key: "thirdLevel",
        defaultValue: ["allThirdLevel"],
        options: [{ value: "allThirdLevel", label: "全部三级渠道" }],
      },
    ],
    // 金额字段（新接口字段名，单位：分，需要转换为元）
    moneyKeys: [
      "totalIncome",
      "newUserIncome",
      "refundIncome",
      "newUserAdIncome",
      "adIncome",
      "totalIncomeAndroid",
      "totalIncomeIOS",
      "totalIncomeOther",
    ],
    // 整数字段（不需要小数）
    integerKeys: ["newUsers", "dau"],
    dateBtns: [
      { key: "0", val: "今天" },
      { key: "-1", val: "昨天" },
      { key: "-7", val: "7天" },
      { key: "-30", val: "30天" },
    ],
    dateDefaultValue: "0",
    targetBtns: [
      { key: "newUsers", val: "全部新增" },
      { key: "dau", val: "活跃人数" },
      { key: "totalIncome", val: "全部内购收入" },
      { key: "totalIncomeAndroid", val: "安卓内购收入" },
      { key: "totalIncomeIOS", val: "IOS内购收入" },
      { key: "newUserIncome", val: "新增用户内购收入" },
      // { key: "totalIncomeOther", val: "其他内购收入" },
      // { key: "refundIncome", val: "退款金额" },
      {
        key: "newUserAdIncome",
        val: "新增广告收入",
      },
      {
        key: "newUserAdTimes",
        val: "新增广告次数",
      },
      {
        key: "adIncome",
        val: "全部广告收入",
      },
      {
        key: "adTimes",
        val: "广告次数",
      },
    ],
    targetDefaultValue: "newUsers",
    otherTargetDefaultValue: "one_day_retain_num",
    echartsOptions: {
      feilds: {},
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
        formatter: (params) => {
          let template = ``;
          const feilds = this.state.echartsOptions.feilds;

          template = `<h3 style="color:#fff">${params[0].axisValue}</h3>`;
          params.forEach((val) => {
            const currentVal = val.value[val.seriesName] || 0;

            if (currentVal > 0) {
              template += `
                            <div>${val.marker}${
                feilds
                  ? feilds[val.seriesName] || val.seriesName
                  : val.seriesName
              } : ${currentVal}</div>
                        `;
            }
          });
          return template;
        },
      },
      dataset: {
        dimensions: ["date"],
        source: [],
      },
    },
    typeBtns: [
      { key: "time", val: "按时间汇总" },
      // { key: "game", val: "按产品汇总" },
      { key: "channel", val: "按渠道汇总" },
    ],
    typeDefaultValue: "time",
    tableData: [],
    tableHeadData: [],
    isOneday: false,
    isDeducted: false,
    isAllowUse: false,
    expandedRowKeys: [], // 控制展开的行
    // 添加缓存对象，用于缓存不同查询类型的数据
    tableDataCache: {}, // 格式：{ 'time': { data: [], head: [], rawData: [] }, 'channel': {...}, 'game': {...} }
    chartRawData: [], // 缓存图表原始数据，用于切换指标时无需重新请求
  };

  // 客户端导出表格为 CSV
  handleDownload = () => {
    const { tableData, tableHeadData } = this.state;

    if (!tableData || tableData.length === 0) {
      message.error("暂无数据可下载");
      return;
    }

    // 展开树形数据
    const flattenData = this.flattenTableData(tableData);

    // 生成 CSV 内容
    const csvContent = this.generateCSV(flattenData, tableHeadData);

    // 下载文件
    const timestamp = formatDate(Date.now(), "yyyyMMdd_hhmmss");
    const typeLabel =
      this.state.typeDefaultValue === "time" ? "按时间汇总" : "按渠道汇总";
    const fileName = `统计数据_${typeLabel}_${timestamp}.csv`;

    this.downloadCSV(csvContent, fileName);
    message.success("下载成功");
  };

  // 展开树形表格数据
  flattenTableData = (data, level = 0) => {
    const result = [];
    data.forEach((item) => {
      // 复制数据，避免修改原数据
      const row = { ...item, _level: level };
      delete row.children;
      delete row.key;
      delete row.isSummary;
      delete row.isChannelSummary;
      delete row.isTotalSummary;
      result.push(row);

      // 递归处理子节点
      if (item.children && item.children.length > 0) {
        result.push(...this.flattenTableData(item.children, level + 1));
      }
    });
    return result;
  };

  // 生成 CSV 内容
  generateCSV = (data, headers) => {
    // 添加 BOM 头以支持中文
    let csv = "\uFEFF";

    // 生成表头
    const headerRow = headers.map((h) => `"${h.title}"`).join(",");
    csv += headerRow + "\n";

    // 生成数据行
    data.forEach((row) => {
      const rowData = headers.map((h) => {
        const value = row[h.dataIndex];
        // 处理特殊字符
        if (value === undefined || value === null) return '""';
        const strValue = String(value).replace(/"/g, '""');
        return `"${strValue}"`;
      });
      csv += rowData.join(",") + "\n";
    });

    return csv;
  };

  // 下载 CSV 文件
  downloadCSV = (content, fileName) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * @desc 当点击一级渠道时 筛出二级渠道
   * @param {*} val
   * @param {*} lvl
   */
  handleChannelLinkage = (val) => {
    let tempAllSelect = this.state.allSelect;
    tempAllSelect[2].options = [
      // { value: "allSecondLevel", label: "全部二级渠道" },
    ];

    const secondChannel = store.remote.secondChannel;
    secondChannel.forEach((ele) => {
      tempAllSelect[2].options.push({
        value: ele.id,
        label: ele.name,
      });
    });
    this.setState({ allSelect: tempAllSelect });
  };

  /**
   * @desc 当点击二级渠道时 筛出三级渠道
   * @param {*} val
   */
  handleSecondChannelToThirdChannel = (val) => {
    let tempAllSelect = this.state.allSelect;
    let tempArr = [];
    const thirdChannel = store.remote.thirdChannel;

    if (val) {
      tempArr = thirdChannel.map((el) => ({ value: el.id, label: el.name }));
    }
    tempAllSelect[3].options = [
      { value: "allThirdLevel", label: "全部三级渠道" },
      ...tempArr,
    ];

    this.setState({ allSelect: tempAllSelect });
  };

  handleChange = async (val, key) => {
    const tempSelectedVal = this.state.selectedVal;
    const tempAllSelect = this.state.allSelect;

    if (key === "game") {
      // 处理"全部游戏"选项的逻辑
      const allGamesValue = "allGames";
      const hasAllGames = val.includes(allGamesValue);
      const hadAllGames = tempSelectedVal.game.includes(allGamesValue);

      let newVal = val;
      if (hasAllGames && !hadAllGames) {
        // 新选中了"全部游戏"，只保留"全部游戏"
        newVal = [allGamesValue];
      } else if (hasAllGames && val.length > 1) {
        // 已经选中"全部游戏"，又选了其他游戏，取消"全部游戏"
        newVal = val.filter((v) => v !== allGamesValue);
      }

      tempAllSelect[0].defaultValue = newVal;
      tempSelectedVal.game = newVal;

      // 游戏改变时重置自定义维度
      this.setState({ customDimension: "" });
    }
    if (key === "firstLevel") {
      await this.getSecondChanelById(val);
      tempAllSelect[1].defaultValue = val;
      tempAllSelect[2].defaultValue = "";
      tempAllSelect[3].defaultValue = ["allThirdLevel"];

      Object.assign(tempSelectedVal, {
        firstLevel: val,
        secondLevel: "",
        thirdLevel: ["allThirdLevel"],
      });

      this.handleChannelLinkage(val);
      //   this.getAllTarget({
      //     firstChannel: val,
      //   });
    }
    if (key === "secondLevel") {
      await this.getThirdChannelById(val);
      tempAllSelect[2].defaultValue = val;
      tempAllSelect[3].defaultValue = ["allThirdLevel"];

      Object.assign(tempSelectedVal, {
        secondLevel: val,
        thirdLevel: ["allThirdLevel"],
      });

      this.handleSecondChannelToThirdChannel(val);

      // 二级渠道改变时重置自定义维度
      this.setState({ customDimension: "" });
    }
    if (key === "thirdLevel") {
      tempAllSelect[3].defaultValue = val.length === 0 ? [] : val;
      tempSelectedVal.thirdLevel = val;
    }
    this.setState({
      allSelect: tempAllSelect,
      selectedVal: tempSelectedVal,
    });
  };

  onDateChange = (data, btntag) => {
    let tempSelectedVal = this.state.selectedVal;
    tempSelectedVal.time = data;
    let isOneDay = data.end - data.start > 86400 ? false : true;
    this.setState({
      isOneday: isOneDay,
      tagType: isOneDay ? true : this.state.tagType,
      dateDefaultValue: btntag,
      selectedVal: tempSelectedVal,
    });
  };

  handleTargetRadioBtnChange = (data) => {
    const stateKey = this.state.tagType
      ? "targetDefaultValue"
      : "otherTargetDefaultValue";
    this.setState({ [stateKey]: data }, () => {
      // 使用缓存的图表原始数据重新渲染，无需重新请求
      if (this.state.chartRawData && this.state.chartRawData.length > 0) {
        this.renderChartFromCache();
      }
    });
  };

  // 平台切换处理
  handleSystemChange = (value) => {
    this.setState({ system: value }, () => {
      // 平台切换后，如果已有数据则重新请求
      if (this.state.chartRawData && this.state.chartRawData.length > 0) {
        // 清空缓存并重新请求
        this.setState({
          tableDataCache: {},
          chartRawData: [],
          isChartLoading: true,
          isTableLoading: true,
        });
        const params = this.handleParams();
        this.fetchAllData(params);
      }
    });
  };

  // 获取自定义维度选项（根据游戏和二级渠道判断）
  getCustomDimensionOptions = () => {
    const { game, secondLevel } = this.state.selectedVal;

    // 选了全部游戏或没选游戏时不展示
    if (!game || game.length === 0 || game.includes("allGames")) {
      return null;
    }

    // 获取二级渠道名称
    const secondChannelList = store.remote.secondChannel || [];
    const secondChannelInfo = secondChannelList.find(
      (ch) => ch.id === secondLevel
    );
    const secondChannelName = secondChannelInfo ? secondChannelInfo.name : "";

    // 遍历配置，找到匹配的游戏和渠道
    for (const gameKey of Object.keys(this.customDimensionConfig)) {
      const config = this.customDimensionConfig[gameKey];
      // 检查是否有任何选中的游戏包含该 gameKey
      const hasMatchingGame = game.some((g) => g.includes(gameKey));
      if (hasMatchingGame && secondChannelName === config.channelName) {
        return config.options;
      }
    }

    return null;
  };

  // 自定义维度切换处理
  handleCustomDimensionChange = (value) => {
    this.setState({ customDimension: value }, () => {
      // 切换后，如果已有数据则重新请求
      // 清空缓存并重新请求
      this.setState({
        tableDataCache: {},
        chartRawData: [],
        isChartLoading: true,
        isTableLoading: true,
      });
      const params = this.handleParams();
      this.fetchAllData(params);
    });
  };

  // 获取图表系列名称
  getChartSeriesName = () => {
    const { game } = this.state.selectedVal;
    if (!game || game.length === 0) return "数据";
    if (game.includes("allGames")) return "全部游戏";
    if (game.length === 1) return jsonAllGames[game[0]] || game[0];
    return "多游戏汇总";
  };

  // 从缓存数据重新渲染图表
  renderChartFromCache = () => {
    const { chartRawData, echartsOptions } = this.state;
    const seriesName = this.getChartSeriesName();

    let temp = { ...echartsOptions };
    temp.dataset.source = this.handleChartsData(chartRawData);
    temp.feilds = {};
    temp.dataset.dimensions = ["date"].concat([seriesName]);
    temp.feilds[seriesName] = seriesName;
    temp.yAxis = {};

    this.setState({ echartsOptions: temp }, () => {
      if (temp.dataset.source.length === 0) return;
      bus.dispatch("echarts_line");
    });
  };

  handleTypeRadioBtnChange = (data) => {
    this.setState({ typeDefaultValue: data }, () => {
      // 检查缓存，如果有缓存就使用缓存数据，避免重新请求
      const cache = this.state.tableDataCache[data];

      if (cache && cache.rawData && cache.rawData.length > 0) {
        // 使用缓存的原始数据重新处理表格
        this.handleSumData(cache.rawData);
      } else {
        let params = this.handleParams();
        this.fetchTableData(params, data);
      }
    });
  };

  // 单独获取表格数据（切换汇总类型时使用）
  fetchTableData = async (params, type) => {
    this.setState({ isTableLoading: true });
    const tempParams = JSON.parse(JSON.stringify(params));
    const start = formatDate(tempParams.start * 1000, "yyyyMMdd");
    // DateGroup 组件的 end 多加了一天，这里需要减回来
    const end = formatDate(tempParams.end * 1000 - 86400000, "yyyyMMdd");

    const gameIdList = tempParams.gameIds || [];

    const basePayload = {
      start,
      end,
      gameId: gameIdList, // 接口支持字符串数组
      channel: tempParams.topChannel || "",
      subChannel: tempParams.secondChannel || "",
      thirdChannels: tempParams.subChannels || [],
      system: this.state.system || undefined, // 平台筛选
      customDimension: this.state.customDimension || undefined, // 自定义维度筛选
    };

    const apiFn =
      type === "channel" ? getStatisticsByChannel : getStatisticsByDate;

    try {
      const ret = await apiFn(basePayload);
      const { ok, msg, list } = this.extractNewStatListFromResponse(ret);

      if (!ok) {
        message.error(msg);
        this.setState({ isTableLoading: false });
        return;
      }

      const allRows = (list || []).map((item) =>
        this.normalizeNewStatRow(item)
      );
      const rows = this.aggregateNewStatRows(allRows);

      // 更新缓存
      const newCache = {
        ...this.state.tableDataCache,
        [type]: { rawData: rows },
      };
      this.setState({ tableDataCache: newCache });

      this.handleSumData(rows);
    } catch (e) {
      console.error(e);
      message.error("请求失败");
      this.setState({ isTableLoading: false });
    }
  };

  getFirstChannel = async () => {
    const ret = await getChannelByLvl(1);
    if (ret && ret.data && ret.data.data) {
      const resp = ret.data.data || [];
      store.sync({
        firstChannel: resp,
      });
      let initOpt = [{ value: "allFirstLevel", label: "全部一级渠道" }];
      // this.setState({ allChannel: [] });
      this.setFormData(this.state.allSelect, "firstLevel", resp, initOpt);
    }
  };

  getSecondChanelById = async (parentId) => {
    const ret = await getChannelByLvl(2, parentId);
    if (ret && ret.data && ret.data.data) {
      const resp = ret.data.data || [];
      store.sync({
        secondChannel: resp,
      });
    }
  };

  getThirdChannelById = async (parentId) => {
    const ret = await getChannelByLvl(3, parentId);
    if (ret && ret.data && ret.data.data) {
      const resp = ret.data.data || [];
      store.sync({
        thirdChannel: resp,
      });
    }
  };

  setFormData = (data, key, options, initOpt) => {
    const tempOptions = options || [];
    const tempData = data;
    const tempSelected = this.state.selectedVal;
    const { roleTp, firstLevelChannelIds } = bus.me.data.accountInfo;
    function isShowFirst(roleTp) {
      return roleTp === 5 || roleTp === 6;
    }
    tempData.forEach((item) => {
      if (item.key === key) {
        item.options = isShowFirst(roleTp) ? [] : initOpt.slice();
        item.options = item.options.concat(
          tempOptions.map((item) => {
            return { value: item.id, label: item.name };
          })
        );

        // if (bus.userInfo.isDeducted && item.options.length > 0) {
        //   item.defaultValue = item.options[0].value;
        //   tempSelected.firstLevel = item.defaultValue;
        //   this.handleChannelLinkage(item.defaultValue);
        // }
      }
    });

    this.setState({ allSelect: tempData, selectedVal: tempSelected });
    if (isShowFirst(roleTp)) {
      // 二级渠道管理员 或者 三级渠道管理员
      this.initFirstChannel(firstLevelChannelIds[0]);
    }
  };

  getAllGames = () => {
    getAllGames().then((res) => {
      const games = res.data.data || [];
      let tempData = [];
      jsonAllGames = {};

      const specialAccount = [
        "695cc2830e74c9420cc5f54d",
        "695cc1cc0e74c9420cc5f54c",
      ];

      const myAccountId = bus.me.data.accountInfo.id;

      games.forEach((item) => {
        if (!jsonAllGames[item.gameId]) {
          tempData.push({
            value: item.gameId,
            label: item.gameName || item.gameId,
          });
          jsonAllGames[item.gameId] = item.gameName;
        }
      });

      if (specialAccount.includes(myAccountId)) {
        tempData = tempData.filter((item) => {
          return ["fejsfmerge", "pirate-minigame"].includes(item.value);
        });
      }

      let tempAllSelect = this.state.allSelect;

      // 添加"全部游戏"选项在最前面
      tempAllSelect[0].options = [
        { value: "allGames", label: "全部游戏" },
        ...tempData,
      ];
      this.setState({
        allGames: tempData, // allGames 保存原始游戏列表（不含"全部游戏"选项）
        allSelect: tempAllSelect,
      });
    });
  };

  handleParams = () => {
    let params = {};
    const tempVal = this.state.selectedVal;

    if (tempVal.time.start !== 0) {
      Object.assign(params, tempVal.time);
    }
    if (tempVal.game && tempVal.game.length > 0) {
      // 多选：检查是否选中了"全部游戏"
      let gameIds = tempVal.game;
      if (tempVal.game.includes("allGames")) {
        // 选中"全部游戏"时，传递所有游戏ID
        gameIds = this.state.allGames.map((g) => g.value);
      }
      Object.assign(params, { gameIds });
    }
    if (tempVal.firstLevel && tempVal.firstLevel.length !== 0) {
      let tempFirst = tempVal.firstLevel;

      if (tempFirst === "allFirstLevel") {
        tempFirst = "";
      }
      Object.assign(params, { topChannel: tempFirst });
    }
    if (tempVal.secondLevel && tempVal.secondLevel.length !== 0) {
      let tempSecond = tempVal.secondLevel;

      if (tempSecond === "allSecondLevel") {
        tempSecond = "";
      }
      Object.assign(params, { secondChannel: tempSecond });
    }
    if (tempVal.thirdLevel && tempVal.thirdLevel.length !== 0) {
      let arr = tempVal.thirdLevel;

      if (tempVal.thirdLevel.includes("allThirdLevel")) {
        arr = [];
      }
      Object.assign(params, { subChannels: arr });
    }

    return params;
  };

  handleChartsData(data) {
    if (!data) return [];
    const currentKey = this.state.tagType
      ? this.state.targetDefaultValue
      : this.state.otherTargetDefaultValue;
    const seriesName = this.getChartSeriesName();

    // 找到当前选中的按钮配置（可能有calc函数）
    const currentBtn = this.state.targetBtns.find(
      (btn) => btn.key === currentKey
    );

    const tempData = data.map((ele) => {
      let itemjson = {};

      // 处理日期
      if (ele.dh) {
        itemjson.date = ele.dh + ":00:00";
      } else if (ele.dt) {
        itemjson.date = ele.dt;
      } else if (ele.date) {
        itemjson.date = formatDateStr(String(ele.date));
      }

      // 计算当前指标的值
      let tempValue = 0;
      if (currentBtn && currentBtn.calc) {
        // 先格式化字段再计算
        const formattedRow = this.formatRowFields(ele);
        tempValue = currentBtn.calc(formattedRow);
      } else {
        tempValue = this.formatFieldValue(currentKey, ele[currentKey]);
      }

      itemjson[seriesName] = tempValue;
      return itemjson;
    });

    // 按日期升序排列
    tempData.sort((a, b) => new Date(a.date) - new Date(b.date));
    return tempData;
  }

  extractNewStatListFromResponse = (res) => {
    const meta =
      (res && res.data && res.data.meta) ||
      (res && res.data && res.data.data && res.data.data.meta);

    if (meta && typeof meta.errCode !== "undefined" && meta.errCode !== 0) {
      return { ok: false, msg: meta.errMsg || "请求失败", list: [] };
    }

    const data = res && res.data && res.data.data;
    const list = Array.isArray(data)
      ? data
      : data && Array.isArray(data.data)
      ? data.data
      : [];

    return { ok: true, msg: "", list };
  };

  normalizeNewStatRow = (row, fallbackGameId) => {
    const item = { ...(row || {}) };

    // 兼容新接口 date: YYYYMMDD -> dt: YYYY-MM-DD
    if (item.date && !item.dt) {
      item.dt = formatDateStr(String(item.date));
    }

    // 兼容新接口 gameId -> game_id
    if (item.gameId && !item.game_id) {
      item.game_id = item.gameId;
    }
    if (fallbackGameId && !item.game_id) {
      item.game_id = fallbackGameId;
    }

    // 兼容新接口字段命名，映射到当前页面使用的旧字段名
    const fieldMap = {
      new_user_num: "newUsers",
      new_user_pay_money: "newUserIncome",
      pay_money: "totalIncome",
      refund_money: "refundIncome",
      ad_income: "adIncome",
      new_user_ad_income: "newUserAdIncome",
      ad_times: "adTimes",
      new_user_ad_times: "newUserAdTimes",
    };

    Object.keys(fieldMap).forEach((oldKey) => {
      const newKey = fieldMap[oldKey];
      if (
        typeof item[oldKey] === "undefined" &&
        typeof item[newKey] !== "undefined"
      ) {
        item[oldKey] = item[newKey];
      }
    });

    return item;
  };

  aggregateNewStatRows = (rows) => {
    const type = this.state.typeDefaultValue;
    const isChannel = type === "channel";

    // 按渠道汇总时，保留 channelName 用于显示
    const dimKeys = isChannel
      ? ["channel", "channelName", "dt", "dh"]
      : ["dt", "dh"];
    const ignoreKeys = new Set([
      "id",
      "createdAt",
      "update_time",
      "latitude",
      "system",
      "gameId",
      "date",
    ]);

    const map = {};

    (rows || []).forEach((raw) => {
      const item = raw || {};
      const timeKey = item.dh || item.dt || "";
      const key = isChannel
        ? `${item.channel || ""}__${timeKey}`
        : `${timeKey}`;

      if (!key) return;

      if (!map[key]) {
        map[key] = {};
        dimKeys.forEach((k) => {
          if (typeof item[k] !== "undefined") map[key][k] = item[k];
        });
        // 保留一些非数值维度字段（如 channel_1/2/3）
        Object.keys(item).forEach((k) => {
          if (dimKeys.includes(k) || ignoreKeys.has(k)) return;
          const v = item[k];
          const num = typeof v === "number" ? v : parseFloat(v);
          if (isNaN(num) && typeof map[key][k] === "undefined") {
            map[key][k] = v;
          }
        });
      }

      Object.keys(item).forEach((k) => {
        if (dimKeys.includes(k) || ignoreKeys.has(k)) return;
        const v = item[k];
        const num = typeof v === "number" ? v : parseFloat(v);
        if (isNaN(num)) return;
        const prev =
          typeof map[key][k] === "number"
            ? map[key][k]
            : parseFloat(map[key][k]) || 0;
        map[key][k] = prev + num;
      });
    });

    return Object.values(map);
  };

  handleSumData = (val) => {
    let tempJson = {};
    this.state.targetBtns.forEach((item) => {
      tempJson[item.key] = item.val;
    });
    tempJson.game_id = "游戏Id";
    tempJson.channel_1 = "一级渠道";
    tempJson.channel_2 = "二级渠道";
    tempJson.channel_3 = "三级渠道";
    tempJson.channelName = "渠道"; // 使用 channelName 作为渠道列

    let data = val
      .map((item) => {
        // 使用统一的字段格式化方法
        const formattedItem = this.formatRowFields(item);

        // 处理时间字段
        if (formattedItem.dh) {
          formattedItem.dh = formattedItem.dh.split(" ")[1] + "时";
          delete formattedItem.dt;
        } else if (formattedItem.dt) {
          if (
            this.state.typeDefaultValue !== "time" &&
            this.state.typeDefaultValue !== "channel"
          ) {
            delete formattedItem.dt;
          }
          delete formattedItem.dh;
        }

        // 清理不需要的字段
        delete formattedItem.latitude;
        delete formattedItem.update_time;
        delete formattedItem.channel; // 删除 channel，使用 channelName 显示
        if (this.state.typeDefaultValue !== "game")
          delete formattedItem.game_id;
        if (this.state.typeDefaultValue !== "channel") {
          delete formattedItem.channelName;
        }

        return formattedItem;
      })
      .sort((a, b) => {
        // 按时间汇总时使用倒序（最新的在前），其他情况使用升序
        if (this.state.typeDefaultValue === "time") {
          return a.dh
            ? b.dh.slice(0, 2) - a.dh.slice(0, 2)
            : new Date(b.dt) - new Date(a.dt);
        } else {
          return a.dh
            ? a.dh.slice(0, 2) - b.dh.slice(0, 2)
            : new Date(a.dt) - new Date(b.dt);
        }
      });

    let tempHeadData = [];
    const dataKeys = Object.keys(data[0] || {});

    // 定义表头顺序：先是时间/渠道列，然后按照 targetBtns 的顺序
    const orderedKeys = [];

    // 先添加时间/渠道相关列
    if (dataKeys.includes("dt")) orderedKeys.push("dt");
    if (dataKeys.includes("dh")) orderedKeys.push("dh");
    if (dataKeys.includes("channelName")) orderedKeys.push("channelName");
    if (dataKeys.includes("game_id")) orderedKeys.push("game_id");
    if (dataKeys.includes("channel_1")) orderedKeys.push("channel_1");
    if (dataKeys.includes("channel_2")) orderedKeys.push("channel_2");
    if (dataKeys.includes("channel_3")) orderedKeys.push("channel_3");

    // 按照 targetBtns 定义的顺序添加指标列
    this.state.targetBtns.forEach((btn) => {
      if (dataKeys.includes(btn.key)) {
        orderedKeys.push(btn.key);
      }
    });

    // 添加其他未在 targetBtns 中定义的列
    dataKeys.forEach((key) => {
      if (!orderedKeys.includes(key)) {
        orderedKeys.push(key);
      }
    });

    orderedKeys.forEach((key) => {
      // 按渠道汇总时，不显示dt和dh列（时间信息已在渠道列中显示）
      if (
        this.state.typeDefaultValue === "channel" &&
        (key === "dt" || key === "dh")
      ) {
        return;
      }

      let title = tempJson[key]
        ? tempJson[key]
        : key === "dt"
        ? "日期"
        : key === "dh"
        ? "时间"
        : "";
      if (title)
        tempHeadData.push({
          title,
          dataIndex: key,
          key: key,
          align: "left",
          width: "150px",
        });
    });

    // 处理按时间汇总：添加汇总行
    if (this.state.typeDefaultValue === "time" && data.length > 0) {
      data = this.generateTimeSummaryData(data, tempHeadData);
    }

    // 处理按渠道汇总：添加总汇总行和树形结构
    if (this.state.typeDefaultValue === "channel" && data.length > 0) {
      data = this.generateChannelSummaryData(data, tempHeadData);
    }

    this.setState({
      tableData: data,
      tableHeadData: tempHeadData,
      isTableLoading: false,
      expandedRowKeys: [], // 每次更新数据时重置展开状态
    });
  };

  // 生成按时间汇总的数据（带汇总行）
  generateTimeSummaryData = (data, tempHeadData) => {
    if (data.length === 0) return data;

    // 计算汇总数据
    const summaryRow = {};
    const numericKeys = tempHeadData
      .map((h) => h.dataIndex)
      .filter((key) => key !== "dt" && key !== "dh");

    numericKeys.forEach((key) => {
      const sum = data.reduce((acc, item) => {
        const val = parseFloat(item[key]) || 0;
        return acc + val;
      }, 0);
      // 使用统一的格式化逻辑
      const { moneyKeys, integerKeys } = this.state;
      if (
        moneyKeys.includes(key) ||
        key === "adIncome" ||
        key === "newUserAdIncome"
      ) {
        summaryRow[key] = sum.toFixed(2);
      } else if (integerKeys.includes(key)) {
        summaryRow[key] = Math.round(sum);
      } else {
        summaryRow[key] = Number.isInteger(sum) ? sum : sum.toFixed(2);
      }
    });

    // 设置日期范围（倒序排列：第一条是最新，最后一条是最早）
    const latestDate = data[0].dt || data[0].dh;
    const earliestDate = data[data.length - 1].dt || data[data.length - 1].dh;

    if (data[0].dt) {
      summaryRow.dt = `${earliestDate} ~ ${latestDate}`;
    } else if (data[0].dh) {
      summaryRow.dh = `${earliestDate} ~ ${latestDate}`;
    }

    // 设置唯一key和children
    summaryRow.key = "summary";
    summaryRow.children = data.map((item, index) => ({
      ...item,
      key: `detail_${index}`,
    }));

    // 汇总行样式标记
    summaryRow.isSummary = true;

    return [summaryRow];
  };

  // 处理展开/收起行（优化：使用 Set 提高查找效率）
  handleExpandRow = (record) => {
    const { expandedRowKeys } = this.state;
    const key = record.key;

    // 如果该行没有children，不处理
    if (!record.children || record.children.length === 0) {
      return;
    }

    // 使用 Set 提高查找效率
    const expandedSet = new Set(expandedRowKeys);

    if (expandedSet.has(key)) {
      // 已展开，收起
      expandedSet.delete(key);
    } else {
      // 未展开，展开
      expandedSet.add(key);
    }

    this.setState({ expandedRowKeys: Array.from(expandedSet) });
  };

  // 缓存 onRow 回调，避免每次渲染创建新函数
  // 使用 expandRowByClick 后只需要设置样式
  getRowProps = (record) => {
    const hasChildren = record.children && record.children.length > 0;
    return {
      style: {
        cursor: hasChildren ? "pointer" : "default",
      },
    };
  };

  // 缓存 rowClassName 回调
  getRowClassName = (record) => {
    if (record.isTotalSummary) return "total-summary-row";
    if (record.isChannelSummary) return "channel-summary-row";
    if (record.isSummary) return "summary-row";
    return "";
  };

  // 缓存 onExpand 回调
  handleTableExpand = (expanded, record) => {
    const { expandedRowKeys } = this.state;
    const newExpandedRowKeys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter((key) => key !== record.key);
    this.setState({ expandedRowKeys: newExpandedRowKeys });
  };

  // 缓存 rowKey 生成函数
  getRowKey = (record) => {
    return (
      record.key ||
      record["channelName"] ||
      record["game_id"] ||
      record["dt"] ||
      record["dh"]
    );
  };

  // 展开图标样式（静态，避免重复创建对象）
  expandIconStyle = {
    cursor: "pointer",
    fontSize: 14,
    fontWeight: "bold",
    color: "#000",
    marginRight: 8,
    transition: "transform 0.2s",
  };

  expandIconPlaceholderStyle = { display: "inline-block", width: 17 };

  // 自定义展开图标（优化：减少内联函数）
  customExpandIcon = (props) => {
    const { expanded, onExpand, record } = props;

    // 如果该行没有children，不显示图标
    if (!record.children || record.children.length === 0) {
      return <span style={this.expandIconPlaceholderStyle} />;
    }

    const IconComponent = expanded ? DownOutlined : RightOutlined;
    return (
      <IconComponent
        onClick={(e) => {
          e.stopPropagation();
          onExpand(record, e);
        }}
        style={this.expandIconStyle}
      />
    );
  };

  // 生成按渠道汇总的树形数据（带总汇总行）
  generateChannelSummaryData = (data, tempHeadData) => {
    if (data.length === 0) return data;

    // 按渠道分组（使用 channelName 作为分组键）
    const channelGroups = {};
    data.forEach((item) => {
      const channelName = item.channelName || item.channel || "";
      if (!channelGroups[channelName]) {
        channelGroups[channelName] = [];
      }
      channelGroups[channelName].push(item);
    });

    // 计算每个渠道的汇总
    const channelSummaries = Object.keys(channelGroups).map(
      (channelName, idx) => {
        const items = channelGroups[channelName];
        const summaryRow = { channelName };

        const numericKeys = tempHeadData
          .map((h) => h.dataIndex)
          .filter(
            (key) => key !== "channelName" && key !== "dt" && key !== "dh"
          );

        numericKeys.forEach((key) => {
          const sum = items.reduce((acc, item) => {
            const val = parseFloat(item[key]) || 0;
            return acc + val;
          }, 0);
          // 使用统一的格式化逻辑
          const { moneyKeys, integerKeys } = this.state;
          if (
            moneyKeys.includes(key) ||
            key === "adIncome" ||
            key === "newUserAdIncome"
          ) {
            summaryRow[key] = sum.toFixed(2);
          } else if (integerKeys.includes(key)) {
            summaryRow[key] = Math.round(sum);
          } else {
            summaryRow[key] = Number.isInteger(sum) ? sum : sum.toFixed(2);
          }
        });

        summaryRow.key = `channel_${idx}`;

        // 先对items按时间倒序排列（最新的在前）
        const sortedItems = items.sort((a, b) => {
          if (a.dh && b.dh) {
            return b.dh.localeCompare(a.dh);
          } else if (a.dt && b.dt) {
            return new Date(b.dt) - new Date(a.dt);
          }
          return 0;
        });

        // 将明细行的channelName字段替换为时间字段（dt或dh）
        summaryRow.children = sortedItems.map((item, index) => {
          const childItem = { ...item };
          // 将渠道列显示为时间
          if (childItem.dt) {
            childItem.channelName = childItem.dt;
          } else if (childItem.dh) {
            childItem.channelName = childItem.dh;
          }
          childItem.key = `channel_${idx}_${index}`;
          return childItem;
        });
        summaryRow.isChannelSummary = true;

        return summaryRow;
      }
    );

    // 计算总汇总
    const totalSummary = { channelName: "总计" };
    const numericKeys = tempHeadData
      .map((h) => h.dataIndex)
      .filter((key) => key !== "channelName" && key !== "dt" && key !== "dh");

    numericKeys.forEach((key) => {
      const sum = data.reduce((acc, item) => {
        const val = parseFloat(item[key]) || 0;
        return acc + val;
      }, 0);
      // 使用统一的格式化逻辑
      const { moneyKeys, integerKeys } = this.state;
      if (
        moneyKeys.includes(key) ||
        key === "adIncome" ||
        key === "newUserAdIncome"
      ) {
        totalSummary[key] = sum.toFixed(2);
      } else if (integerKeys.includes(key)) {
        totalSummary[key] = Math.round(sum);
      } else {
        totalSummary[key] = Number.isInteger(sum) ? sum : sum.toFixed(2);
      }
    });

    totalSummary.key = "total_summary";
    totalSummary.isTotalSummary = true;

    // 总计行单独列在第一行，后面跟各个渠道的汇总行
    return [totalSummary, ...channelSummaries];
  };

  isCanSearch() {
    const {
      game,
      thirdLevel,
      secondLevel,
      time: { start, end },
    } = this.state.selectedVal;
    if (!start || !end) {
      message.error("请选择查询条件(时间)");
      return false;
    }
    const gameIsEmpty = !game || game.length === 0;
    const thirdIsEmpty = thirdLevel.length === 0;
    const secondIsEmpty = !secondLevel || secondLevel.length === 0;
    const errText = `请选择查询条件(${gameIsEmpty ? "游戏" : "三级渠道"})`;

    if (secondIsEmpty) {
      message.error("请选择查询条件(二级渠道)");
      return false;
    }
    if (gameIsEmpty || thirdIsEmpty) {
      message.error(errText);
      return false;
    }
    return true;
  }

  handleSearch = async () => {
    const isCanFlag = this.isCanSearch();
    if (!isCanFlag) {
      return;
    }
    // 查询条件改变时，清空缓存
    this.setState({
      tableDataCache: {},
      chartRawData: [],
      isChartLoading: true,
      isTableLoading: true,
    });

    let params = this.handleParams();
    await this.fetchAllData(params);
  };

  // 统一数据请求方法，避免重复调用接口
  fetchAllData = async (params) => {
    const tempParams = JSON.parse(JSON.stringify(params));
    const start = formatDate(tempParams.start * 1000, "yyyyMMdd");
    // DateGroup 组件的 end 多加了一天，这里需要减回来
    const end = formatDate(tempParams.end * 1000 - 86400000, "yyyyMMdd");

    const gameIdList = tempParams.gameIds || [];

    if (gameIdList.length === 0) {
      // 无游戏选择
      this.setState({ isChartLoading: false, isTableLoading: false });
      return;
    }

    const basePayload = {
      start,
      end,
      gameId: gameIdList, // 接口支持字符串数组，一次性传递多个游戏ID
      channel: tempParams.topChannel || "",
      subChannel: tempParams.secondChannel || "",
      thirdChannels: tempParams.subChannels || [],
      system: this.state.system || undefined, // 平台筛选
      customDimension: this.state.customDimension || undefined, // 自定义维度筛选
    };

    const isChannelType = this.state.typeDefaultValue === "channel";

    try {
      // 按时间汇总数据（用于图表和表格-时间汇总）
      let timeRows = [];
      // 按渠道汇总数据（仅用于表格-渠道汇总）
      let channelRows = [];

      // 并发请求：按时间汇总 + 按渠道汇总（如果需要）
      const timePromise = getStatisticsByDate(basePayload);
      const channelPromise = isChannelType
        ? getStatisticsByChannel(basePayload)
        : Promise.resolve(null);

      const [timeResult, channelResult] = await Promise.all([
        timePromise,
        channelPromise,
      ]);

      // 处理按时间汇总数据
      const {
        ok: timeOk,
        msg: timeMsg,
        list: timeList,
      } = this.extractNewStatListFromResponse(timeResult);
      if (!timeOk) {
        message.error(timeMsg);
        this.setState({ isChartLoading: false, isTableLoading: false });
        return;
      }
      const allTimeRows = (timeList || []).map((item) =>
        this.normalizeNewStatRow(item)
      );
      timeRows = this.aggregateNewStatRows(allTimeRows);

      // 处理按渠道汇总数据（如果有）
      if (channelResult) {
        const {
          ok: channelOk,
          msg: channelMsg,
          list: channelList,
        } = this.extractNewStatListFromResponse(channelResult);
        if (!channelOk) {
          message.error(channelMsg);
          this.setState({ isChartLoading: false, isTableLoading: false });
          return;
        }
        const allChannelRows = (channelList || []).map((item) =>
          this.normalizeNewStatRow(item)
        );
        channelRows = this.aggregateNewStatRows(allChannelRows);
      }

      // 缓存原始数据
      this.setState({ chartRawData: timeRows });

      // 渲染图表（始终使用按时间汇总数据）
      this.renderChart(timeRows);

      // 渲染表格（根据当前类型选择数据源）
      const tableRows = isChannelType ? channelRows : timeRows;
      this.handleSumData(tableRows);

      // 缓存两种类型的数据
      const newCache = {
        time: { rawData: timeRows },
        channel: { rawData: channelRows },
      };
      this.setState({ tableDataCache: newCache });
    } catch (e) {
      console.error(e);
      message.error("请求失败");
      this.setState({ isChartLoading: false, isTableLoading: false });
    }
  };

  // 渲染图表
  renderChart = (rows) => {
    let temp = this.state.echartsOptions;
    const seriesName = this.getChartSeriesName();

    temp.dataset.source = this.handleChartsData(rows);
    temp.feilds = {};
    temp.dataset.dimensions = ["date"].concat([seriesName]);
    temp.feilds[seriesName] = seriesName;
    temp.yAxis = {};

    this.setState({ echartsOptions: temp, isChartLoading: false }, () => {
      if (temp.dataset.source.length === 0) return;
      bus.dispatch("echarts_line");
    });
  };

  initFirstChannel(val) {
    this.handleChange(val, "firstLevel");
  }

  componentWillMount() {
    const tempIsDeducted = false; // bus.userInfo.isDeducted;
    let allowUse = false;
    if (bus.userInfo.secondLevelChannelIds[0] === "636c956d12365f0f861b53c7")
      allowUse = true;
    if (tempIsDeducted) {
      this.setState({
        isDeducted: tempIsDeducted,
        isAllowUse: allowUse,
        dateBtns: [
          { key: "-7", val: "7天" },
          { key: "-30", val: "30天" },
        ],
        dateDefaultValue: "-7",
        typeBtns: [{ key: "time", val: "按时间汇总" }],
      });
    }
  }

  componentDidMount() {
    const { roleTp, firstLevelChannelIds } = bus.me.data.accountInfo;
    const params = {};
    if (roleTp === 5 || roleTp === 6) {
      // 二级渠道管理员 或者 三级渠道管理员
      params.firstChannel = firstLevelChannelIds[0];
    }

    this.getFirstChannel();
    this.getAllGames();
  }

  render() {
    // const columns = [
    //   {
    //     title: "指标",
    //     dataIndex: "target",
    //     align: "left",
    //     width: "40%",
    //   },
    //   {
    //     title: "说明",
    //     dataIndex: "name",
    //     align: "left",
    //     width: "60%",
    //   },
    // ];
    // const data = constants;
    const {
      targetBtns,
      dateBtns,
      dateDefaultValue,
      targetDefaultValue,
      tableHeadData,
      tableData,
      tagType,
      otherTargetDefaultValue,
    } = this.state;

    return (
      <div className="statistics">
        <h3 style={{ marginTop: 12, width: 140 }}>统计汇总-新</h3>
        <h5 style={{ marginTop: 5 }}>所有统计数据有1小时延迟</h5>
        <Card
          style={{
            marginTop: 10,
            borderRadius: 8,
          }}
        >
          <ul className="statistics_select">
            {this.state.allSelect.map((item) => {
              return (
                <li key={item.key} className="s_select_item">
                  <Select
                    optionFilterProp="label"
                    allowClear={false}
                    className="custom-select"
                    maxTagCount={1}
                    mode={item.mode || ""}
                    placeholder={item.placeholder}
                    value={item.defaultValue}
                    onChange={(value) => {
                      this.handleChange(value, item.key);
                    }}
                    style={{ width: "100%" }}
                    showSearch
                  >
                    {item.options.map((ele) => {
                      return (
                        <Option
                          key={ele.value}
                          value={ele.value}
                          label={ele.label}
                          className="custom-select-option"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{ele.label}</span>
                          {item.key === "game" && (
                            <span style={{ color: "gray", fontSize: ".7rem" }}>
                              {ele.value}
                            </span>
                          )}
                        </Option>
                      );
                    })}
                  </Select>
                </li>
              );
            })}
          </ul>
          <div className="time_row">
            <div className="time_row_l">
              <DateGroup
                locale={{
                  ...locale,
                  lang: {
                    ...locale.lang,
                    shortMonths: [
                      "一月",
                      "二月",
                      "三月",
                      "四月",
                      "五月",
                      "六月",
                      "七月",
                      "八月",
                      "九月",
                      "十月",
                      "十一月",
                      "十二月",
                    ],
                  },
                }}
                min={moment("2026-01-01").startOf("day")}
                className="custom-date-group"
                onDateChange={this.onDateChange}
                btns={dateBtns}
                defaultValue={dateDefaultValue}
                isDeducted={false}
              />
            </div>
            <div className="time_row_r">
              <Button
                type="primary"
                onClick={this.handleSearch}
                style={{ borderRadius: 12 }}
              >
                查询
              </Button>
            </div>
          </div>
        </Card>
        <Card style={{ marginTop: 10, borderRadius: 8 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h4>统计</h4>
            {bus.me.data.accountInfo.roleTp === 5 ||
            bus.me.data.accountInfo.roleTp === 6 ? null : (
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              ></div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              flexWrap: "wrap",
            }}
          >
            <span>平台：</span>
            <Select
              className="custom-select"
              placeholder="请选择平台"
              style={{ width: 140 }}
              value={this.state.system}
              onChange={this.handleSystemChange}
            >
              <Option value="">全部</Option>
              <Option value="android">Android</Option>
              <Option value="ios">iOS</Option>
              <Option value="other">其它</Option>
            </Select>
            {this.getCustomDimensionOptions() && (
              <>
                <span style={{ marginLeft: 16 }}>自定义维度：</span>
                <Select
                  className="custom-select"
                  placeholder="请选择入口"
                  style={{ width: 140 }}
                  value={this.state.customDimension}
                  onChange={this.handleCustomDimensionChange}
                >
                  {this.getCustomDimensionOptions().map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <Loading isLoading={this.state.isChartLoading} />
            <Card
              className="chart-card"
              style={{
                width: "100%",
                marginTop: 10,
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  marginTop: 0,
                }}
              >
                <RadioBtnGroup
                  onChange={this.handleTargetRadioBtnChange}
                  defaultValue={
                    tagType ? targetDefaultValue : otherTargetDefaultValue
                  }
                  btns={targetBtns}
                />
              </div>
              <LineDiagram options={this.state.echartsOptions} />
            </Card>
          </div>
          <Card
            className="table-card"
            style={{ marginTop: 10, borderRadius: 8, padding: 0 }}
          >
            <h4>统计详细数据</h4>
            <div
              className="table-container"
              style={{
                overflow: "hidden",
                position: "relative",
              }}
            >
              <RadioBtnGroup
                onChange={this.handleTypeRadioBtnChange}
                defaultValue={this.state.typeDefaultValue}
                btns={this.state.typeBtns}
              />
              <Button
                type="primary"
                style={{
                  marginRight: 8,
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                }}
                onClick={this.handleDownload}
              >
                下载表格
              </Button>
            </div>
            <div className="table-box" style={{ position: "relative" }}>
              <Loading isLoading={this.state.isTableLoading} />
              <VirtualTable
                scroll={{ x: tableHeadData.length * 200, y: TABLE_HEIGHT }}
                columns={tableHeadData}
                dataSource={tableData}
                rowClassName={this.getRowClassName}
                expandable={{
                  expandedRowKeys: this.state.expandedRowKeys,
                  onExpand: this.handleTableExpand,
                }}
              />
            </div>
          </Card>
        </Card>
      </div>
    );
  }
}

